import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { DropdownButton } from "react-bootstrap";

import {
  BatteryFullIcon,
  BatteryHalfIcon,
  BatteryIcon,
  NoWifi,
  WifiIcon,
  BatteryWarningIcon,
  QuestionMarkIcon,
  AngleDownIcon,
  MapMarkerAltIcon,
  FilterIconSolid,
  InfoIcon,
} from "@components/icons";
import HeartBrokenIcon from "@mui/icons-material/HeartBroken";
import Tooltip from "@mui/material/Tooltip";
import ReactTable from "@components/ReactTable";
import { GREEN, RED, GREY, YELLOW } from "@helper/colors";
import Header from "@components/ListHeader/ListHeader";
import { useBackgroundCustomerBasicInfo } from "@hooks/useBackgroundData";
import {
  filterColumnsByPreference,
  normalizeTableColumnPreferences,
  normalizeMobileTableColumnPreferences,
} from "@modules/Customer/utils/tableColumnPreferences";
import useIsMobile from "@hooks/useIsMobile";

const WorkingSensorIcon = ({ title }) => (
  <span
    title={title}
    style={{
      width: "18px",
      height: "18px",
      borderRadius: "999px",
      background: GREEN,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.08)",
      flex: "0 0 auto",
    }}
  >
    <WifiIcon color="#ffffff" width="11px" />
  </span>
);

function getCurrentInstallation(sensor) {
  const installations = Array.isArray(sensor?.installations) ? sensor.installations : [];
  const activeInstallations = installations.filter((installation) => !installation?.removed_at);

  if (activeInstallations.length === 0) {
    return null;
  }

  return activeInstallations.sort((a, b) => {
    const aInstalledAt = a?.installed_at ? new Date(a.installed_at).getTime() : 0;
    const bInstalledAt = b?.installed_at ? new Date(b.installed_at).getTime() : 0;
    return bInstalledAt - aInstalledAt;
  })[0];
}

function getSensorTree(sensor) {
  const currentInstallation = getCurrentInstallation(sensor);
  if (currentInstallation?.tree) {
    return currentInstallation.tree;
  }
  return sensor?.tree && typeof sensor.tree === "object" ? sensor.tree : null;
}

function getSensorTreeName(sensor) {
  const currentTree = getSensorTree(sensor);
  return (
    currentTree?.name || sensor?.tree_name || (typeof sensor?.tree === "string" ? sensor.tree : "")
  );
}

function getSensorAreaId(sensor) {
  const currentTree = getSensorTree(sensor);
  return currentTree?.area_id || currentTree?.area?.id || sensor?.area_id || sensor?.area?.id || "";
}

function getSensorAreaName(sensor) {
  const currentTree = getSensorTree(sensor);
  return (
    currentTree?.area_name ||
    currentTree?.area?.name ||
    sensor?.area_name ||
    sensor?.area?.name ||
    ""
  );
}

function getSensorProjectName(sensor) {
  const currentTree = getSensorTree(sensor);
  return (
    currentTree?.project_name ||
    currentTree?.project?.name ||
    currentTree?.area?.project?.name ||
    sensor?.project_name ||
    sensor?.project?.name ||
    sensor?.area?.project?.name ||
    ""
  );
}

export function statusHeader(t) {
  return {
    Header: (
      <Header
        name={`${t("status")}`}
        infoTitle={t("sensor_status")}
        infoContent={[
          t("sensor_status_description"),
          <div key="working">
            <WorkingSensorIcon title={t("working")} /> - {t("working")}
          </div>,
          <div key="battery_empty">
            <BatteryWarningIcon color={RED} width="15px" title={t("battery_empty")} /> -{" "}
            {t("battery_empty")}
          </div>,
          <div key="maintenance_needed">
            <span title={t("maintenance_needed")} style={{ fontSize: "15px" }}>
              🔧
            </span>{" "}
            - {t("maintenance_needed")}
          </div>,
          <div key="not_sending">
            <NoWifi color={GREY} width="15px" title={t("not_sending")} /> - {t("not_sending")}
          </div>,
          <div key="unknown">
            <QuestionMarkIcon color={GREY} width="15px" title={t("unknown")} /> - {t("unknown")}
          </div>,
          <div key="defect">
            <HeartBrokenIcon sx={{ color: RED, fontSize: "15px" }} title={t("defect")} /> -{" "}
            {t("defect")}
          </div>,
        ]}
      />
    ),
    accessor: "sensor_state",
    style: { minWidth: "130px" },
    Cell: (props) => {
      const hardwareSerial = props.row.original.hardware_serial;
      // let isSendingObj;
      // try {
      //   isSendingObj = JSON.parse(localStorage.getItem("isSending"));
      // } catch (e) {
      //   isSendingObj = null;
      // }
      // const isSendingSensor = isSendingObj && isSendingObj[hardwareSerial] ? true : false;
      const stateMap = {
        0: {
          icon: <WorkingSensorIcon title={t("working")} />,
          label: t("working"),
        },
        1: {
          icon: <BatteryWarningIcon color={RED} width="15px" title={t("battery_empty")} />,
          label: t("battery_empty"),
        },
        2: {
          icon: (
            <span title={t("maintenance_needed")} style={{ fontSize: "15px" }}>
              🔧
            </span>
          ),
          label: t("maintenance_needed"),
        },
        3: (() => {
          let isSendingMap = {};
          try {
            const raw = localStorage.getItem("isSending");
            isSendingMap = raw ? JSON.parse(raw) : {};
          } catch (_) {
            isSendingMap = {};
          }
          return isSendingMap[hardwareSerial]
            ? {
                icon: <WorkingSensorIcon title={t("working")} />,
                label: t("working"),
              }
            : {
                icon: <NoWifi color={GREY} width="15px" title={t("not_sending")} />,
                label: t("not_sending"),
              };
        })(),
        4: {
          icon: <QuestionMarkIcon color={GREY} width="15px" title={t("unknown")} />,
          label: t("unknown"),
        },
        5: {
          icon: <HeartBrokenIcon sx={{ color: RED, fontSize: "18px" }} title={t("defect")} />,
          label: t("defect"),
        },
      };

      const state = stateMap[props.row.original.sensor_state];
      return (
        <div className="d-flex align-items-center gap-1">
          {state?.icon}
          <small className="text-muted">{state?.label}</small>
        </div>
      );
    },
  };
}

export function batteryHeader(t) {
  return {
    Header: `${t("last_battery")}`,
    accessor: "battery",
    Cell: (props) => {
      const battery = props.row.original.battery;
      return (
        <small>
          {battery == undefined ? (
            <BatteryHalfIcon color="#888888" size={"1.3em"} />
          ) : battery > 60 ? (
            <BatteryFullIcon color={GREEN} size={"1.3em"} />
          ) : battery < 30 ? (
            <BatteryIcon color="#CD3100" size={"1.3em"} />
          ) : battery >= 30 && props.row.original.battery <= 60 ? (
            <BatteryHalfIcon color="#FEC400" size={"1.3em"} />
          ) : (
            <BatteryFullIcon color="#888888" size={"1.3em"} />
          )}
          &nbsp; {battery ? `${battery} %` : "?"}
        </small>
      );
    },
  };
}

/**
 * Classifies signal strength into Low/Medium/High based on 7-day average
 * @param {number|null} signalAvg - Weekly signal average (0-31 range for NB-IoT)
 * @returns {{ level: 'low'|'medium'|'high'|'unknown', color: string }}
 */
export function classifySignalStrength(signalAvg) {
  if (signalAvg == null) {
    return { level: "unknown", color: GREY };
  }
  if (signalAvg <= 10) {
    return { level: "low", color: RED };
  }
  if (signalAvg <= 24) {
    return { level: "medium", color: YELLOW };
  }
  return { level: "high", color: GREEN };
}

export function SignalStrengthIndicator({ value, t, showTooltip = false }) {
  const { level, color } = classifySignalStrength(value);

  const labelMap = {
    low: t("signal_low"),
    medium: t("signal_medium"),
    high: t("signal_high"),
    unknown: t("signal_no_data"),
  };

  const indicator = (
    <small className="d-flex align-items-center gap-1">
      <WifiIcon color={color} width="1.3em" />
      <span style={{ color: level === "low" ? RED : level === "unknown" ? GREY : undefined }}>
        {labelMap[level]}
      </span>
      {level === "low" && (
        <InfoIcon
          size="1em"
          color={RED}
          style={{ marginLeft: "2px" }}
          title={t("signal_strength_info")}
        />
      )}
    </small>
  );

  if (showTooltip && level === "low") {
    return (
      <Tooltip title={t("signal_strength_info")} arrow placement="top">
        {indicator}
      </Tooltip>
    );
  }

  return indicator;
}

export function signalStrengthHeader(t) {
  return {
    Header: (
      <Header
        name={t("signal_strength")}
        infoTitle={t("signal_strength")}
        infoContent={[t("signal_strength_info")]}
      />
    ),
    accessor: "weekly_signal_avg",
    Cell: (props) => {
      const sensor = props.row.original;
      // Only show for NB-IoT sensors
      if (sensor.network_type !== "NB") {
        return <small className="text-muted">—</small>;
      }
      return <SignalStrengthIndicator value={sensor.weekly_signal_avg} t={t} showTooltip />;
    },
  };
}

export function keyHeader(t) {
  return {
    Header: (
      <Header
        name={`${t("hardware_serial_imei")}`}
        infoTitle={t("hardware_serial_imei")}
        infoContent={[t("hardware_serial_imei_description")]}
      />
    ),
    accessor: "hardware_serial",
    Cell: (props) => {
      const hardware_serial = props.row.original.hardware_serial;
      const imei = props.row.original.imei;
      const network_type = props.row.original.network_type;
      if (network_type == "NB") {
        return imei;
      } else {
        return hardware_serial;
      }
    },
  };
}

const STATUS_OPTIONS = [
  { id: "0", labelKey: "working" },
  { id: "1", labelKey: "battery_empty" },
  { id: "2", labelKey: "maintenance_needed" },
  { id: "3", labelKey: "not_sending" },
  { id: "4", labelKey: "unknown" },
  { id: "5", labelKey: "defect" },
];

export default function SensorList({
  sensors,
  setOpenForm,
  setIsUpdate,
  setDetail,
  buttons,
  page,
  setPage,
  pageSize,
  setPageSize,
  search,
  setSearch,
  ordering,
  setOrdering,
  selectedAreaIds,
  setSelectedAreaIds,
  selectedStatusIds = [],
  setSelectedStatusIds,
  totalCount,
  hasNext,
  hasPrevious,
  isLoading,
  areaOptions: externalAreaOptions,
}) {
  const { t } = useTranslation();
  const { customerBasicInfo } = useBackgroundCustomerBasicInfo();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const id = query.get("selectedSensor");
  // Debounced search input
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput);
        setPage(1); // Reset to first page on search
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput, search, setSearch, setPage]);

  const areaOptions = useMemo(() => {
    if (Array.isArray(externalAreaOptions) && externalAreaOptions.length > 0) {
      return externalAreaOptions;
    }
    const byId = new Map();
    (sensors || []).forEach((sensor) => {
      const id = String(getSensorAreaId(sensor));
      const name = getSensorAreaName(sensor);
      if (!id || !name || byId.has(id)) return;
      byId.set(id, { id, name });
    });
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [externalAreaOptions, sensors]);

  const filterSensorsByArea = (areaId) => {
    const findIdx = selectedAreaIds.indexOf(areaId);
    if (findIdx > -1) {
      setSelectedAreaIds((prev) => prev.filter((id) => id !== areaId));
    } else {
      setSelectedAreaIds((prev) => [...prev, areaId]);
    }
    setPage(1);
  };

  const filterSensorsByStatus = (statusId) => {
    const findIdx = selectedStatusIds.indexOf(statusId);
    if (findIdx > -1) {
      setSelectedStatusIds((prev) => prev.filter((id) => id !== statusId));
    } else {
      setSelectedStatusIds((prev) => [...prev, statusId]);
    }
    setPage(1);
  };

  const visibleColumnPreferences = useMemo(
    () => normalizeTableColumnPreferences(customerBasicInfo?.table_column_preferences).sensors,
    [customerBasicInfo?.table_column_preferences]
  );

  const mobileColumnPreferences = useMemo(
    () =>
      normalizeMobileTableColumnPreferences(customerBasicInfo?.mobile_table_column_preferences)
        .sensors,
    [customerBasicInfo?.mobile_table_column_preferences]
  );

  const columns = useMemo(
    () => [
      {
        Header: `${t("name")}`,
        id: "name",
        accessor: "name",
        Cell: (props) => {
          const sensor = props.row.original;
          const sensorKey =
            sensor.network_type === "NB"
              ? sensor.imei || sensor.hardware_serial
              : sensor.hardware_serial || sensor.imei;

          return (
            <div>
              <h6 className="mb-0">{sensor.name || "-"}</h6>
              {sensorKey && <span className="text-muted small">{sensorKey}</span>}
            </div>
          );
        },
      },
      {
        Header: `${t("tree")}`,
        id: "tree_name",
        accessor: "tree_name",
        Cell: (props) => {
          const sensor = props.row.original;
          return (
            <div>
              <h6 className="mb-0">{getSensorTreeName(sensor) || ""}</h6>
            </div>
          );
        },
      },
      {
        Header: `${t("area")}`,
        id: "area_name",
        accessor: "area_name",
        Cell: (props) => {
          const sensor = props.row.original;
          return <h6 className="mb-0">{getSensorAreaName(sensor)}</h6>;
        },
      },
      {
        Header: `${t("project.name")}`,
        id: "project_name",
        accessor: "project_name",
        Cell: (props) => {
          const sensor = props.row.original;
          return <h6 className="mb-0">{getSensorProjectName(sensor)}</h6>;
        },
      },
      { ...statusHeader(t), id: "sensor_state" },
      { ...batteryHeader(t), id: "battery" },
      { ...signalStrengthHeader(t), id: "weekly_signal_avg" },
    ],
    [t]
  );

  const visibleColumns = useMemo(() => {
    const preferences = isMobile ? mobileColumnPreferences : visibleColumnPreferences;
    return filterColumnsByPreference(columns, preferences);
  }, [columns, visibleColumnPreferences, mobileColumnPreferences, isMobile]);
  const onRowClicked = (row) => {
    const { id } = row.original;
    const params = new URLSearchParams(location.search);
    params.set("selectedSensor", id);
    navigate(`${location.pathname}?${params.toString()}`);
  };

  const handlePageChange = React.useCallback(
    (newPage) => {
      setPage(newPage);
    },
    [setPage]
  );

  const handlePageSizeChange = React.useCallback(
    (newPageSize) => {
      setPageSize(newPageSize);
      setPage(1);
    },
    [setPageSize, setPage]
  );

  const handleSortChange = React.useCallback(
    (newOrdering) => {
      setOrdering(newOrdering);
      setPage(1); // Reset to first page on sort
    },
    [setOrdering, setPage]
  );

  return (
    <>
      <ReactTable
        buttons={buttons}
        columns={visibleColumns}
        data={sensors}
        onRowClicked={onRowClicked}
        activeId={id}
        pagesize={pageSize}
        size={[10, 20, 30, 40, 50]}
        manualPagination
        pageCount={Math.ceil(totalCount / pageSize)}
        currentPage={page}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSortChange={handleSortChange}
        isLoading={isLoading}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder={t("search_sensor")}
        moreFilter={
          isMobile ? (
            <DropdownButton
              title={
                <div className="d-flex gap-2 align-items-center">
                  <FilterIconSolid size="18px" />
                  {(selectedAreaIds.length > 0 || selectedStatusIds.length > 0) && (
                    <span
                      className="badge bg-primary rounded-pill"
                      style={{ fontSize: "10px", padding: "2px 6px" }}
                    >
                      {selectedAreaIds.length + selectedStatusIds.length}
                    </span>
                  )}
                </div>
              }
              variant="btn-transparent"
            >
              <div className="px-3 py-2">
                <div className="fw-semibold mb-2 small text-muted">{t("area")}</div>
                {areaOptions.map((area) => (
                  <div key={area.id} className="d-flex align-items-center gap-2 mb-1">
                    <input
                      type="checkbox"
                      onChange={() => filterSensorsByArea(area.id)}
                      checked={selectedAreaIds.includes(area.id)}
                    />
                    <small>{area.name}</small>
                  </div>
                ))}
                <hr className="my-2" />
                <div className="fw-semibold mb-2 small text-muted">{t("status")}</div>
                {STATUS_OPTIONS.map((status) => (
                  <div key={status.id} className="d-flex align-items-center gap-2 mb-1">
                    <input
                      type="checkbox"
                      onChange={() => filterSensorsByStatus(status.id)}
                      checked={selectedStatusIds.includes(status.id)}
                    />
                    <small>{t(status.labelKey)}</small>
                  </div>
                ))}
              </div>
            </DropdownButton>
          ) : (
            <>
              <DropdownButton
                title={
                  <div className="d-flex gap-2 align-items-center">
                    <span>{t("area")}</span>
                    <AngleDownIcon />
                  </div>
                }
                variant="btn-transparent"
              >
                {areaOptions.map((area) => (
                  <li key={area.id} className="d-flex align-items-center gap-2 px-2 py-1">
                    <input
                      type="checkbox"
                      onChange={() => filterSensorsByArea(area.id)}
                      checked={selectedAreaIds.includes(area.id)}
                    />
                    <span>{area.name}</span>
                  </li>
                ))}
              </DropdownButton>
              <DropdownButton
                title={
                  <div className="d-flex gap-2 align-items-center">
                    <span>{t("status")}</span>
                    {selectedStatusIds.length > 0 && (
                      <span
                        className="badge bg-primary rounded-pill"
                        style={{ fontSize: "10px", padding: "2px 6px" }}
                      >
                        {selectedStatusIds.length}
                      </span>
                    )}
                    <AngleDownIcon />
                  </div>
                }
                variant="btn-transparent"
              >
                {STATUS_OPTIONS.map((status) => (
                  <li key={status.id} className="d-flex align-items-center gap-2 px-2 py-1">
                    <input
                      type="checkbox"
                      onChange={() => filterSensorsByStatus(status.id)}
                      checked={selectedStatusIds.includes(status.id)}
                    />
                    <span>{t(status.labelKey)}</span>
                  </li>
                ))}
              </DropdownButton>
            </>
          )
        }
      />
    </>
  );
}

export const SensorStatus = ({ color, title }) => {
  return (
    <span
      className={"badge"}
      style={{ padding: "5px", borderRadius: "2px", background: color }}
      title={`${title}`}
    >
      <div
        className="rounded-circle"
        style={{
          width: "6px",
          height: "6px",
          background: "#fff",
        }}
      ></div>
    </span>
  );
};
